import type {
  PlayerViewResponse,
  PlayerCareerStatRow,
  PlayerLeaderRecordFlags,
  PlayerSeasonFlagsRow,
} from '@/api/generated/models'
import type { PlayerCardData } from '../types'
import { mapRatings } from './ratings'
import { mapCareerRow } from './statLines'

// League-leader flag (`ldr*`) and league-record flag (`rec*`) → StatLine key.
// Flags for stats the card doesn't column are skipped.
const LDR_TO_KEY: Partial<Record<keyof PlayerSeasonFlagsRow, string>> = {
  ldrG: 'g', ldrAb: 'ab', ldrR: 'r', ldrH: 'h', ldr2B: 'doubles', ldr3B: 'triples',
  ldrHr: 'hr', ldrRbi: 'rbi', ldrBb: 'bb', ldrSo: 'so', ldrSb: 'sb', ldrCs: 'cs',
  ldrAvg: 'avg', ldrObp: 'obp', ldrSlg: 'slg', ldrOps: 'ops',
  ldrIp: 'ip', ldrW: 'w', ldrL: 'l', ldrSv: 'sv', ldrEr: 'er', ldrGs: 'gs',
  ldrEra: 'era', ldrWhip: 'whip',
}

const REC_TO_KEY: Partial<Record<keyof PlayerSeasonFlagsRow, string>> = {
  recG: 'g', recAb: 'ab', recR: 'r', recH: 'h', rec2B: 'doubles', rec3B: 'triples',
  recHr: 'hr', recRbi: 'rbi', recBb: 'bb', recSo: 'so', recSb: 'sb', recCs: 'cs',
  recAvg: 'avg', recObp: 'obp', recSlg: 'slg', recOps: 'ops',
  recIp: 'ip', recW: 'w', recL: 'l', recSv: 'sv', recEr: 'er', recGs: 'gs',
  recEra: 'era', recWhip: 'whip',
}

// Build a year → [StatLine keys] map from the per-season flag rows using the
// supplied flag→key map. Shared by leaders (`ldr*`) and records (`rec*`).
function mapSeasonFlags(
  flags: PlayerLeaderRecordFlags | undefined,
  flagToKey: Partial<Record<keyof PlayerSeasonFlagsRow, string>>,
): Record<number, string[]> {
  const out: Record<number, string[]> = {}
  for (const row of flags?.seasonRows ?? []) {
    if (row.year == null) continue
    const keys: string[] = []
    for (const [flag, key] of Object.entries(flagToKey)) {
      if (row[flag as keyof PlayerSeasonFlagsRow]) keys.push(key)
    }
    if (keys.length) out[row.year] = keys
  }
  return out
}

export function mapPlayerView(api: PlayerViewResponse): PlayerCardData {
  const b = api.bio
  const isPitcher = api.isPitcher ?? false
  const season = (api.careerStats?.seasonRows ?? []) as PlayerCareerStatRow[]
  const current = season.length ? season[season.length - 1] : undefined
  const career = api.careerStats?.career as PlayerCareerStatRow | undefined

  return {
    id: api.id ?? 0,
    fullName: `${api.firstName ?? ''} ${api.lastName ?? ''}`.trim(),
    firstName: api.firstName ?? '',
    lastName: api.lastName ?? '',
    position: b?.position ?? '',
    isPitcher,
    // Straight through - already routed through the league's ratings strategy
    // server-side, same as `ratings` below.
    positionRatings: api.positionRatings ?? [],
    team: api.teamId != null
      ? { id: api.teamId, name: api.teamName ?? '', city: '', colorPrimary: b?.teamPrimaryColor ?? null, colorSecondary: b?.teamSecondaryColor ?? null }
      : null,
    league: api.leagueId != null
      ? { id: api.leagueId, name: api.leagueName ?? '', type: 0, hasRegularSeason: api.hasRegularSeason ?? false }
      : null,
    bio: {
      age: b?.age,
      bats: b?.bats ?? '',
      throws: b?.throws ?? '',
      height: b?.height ?? '',
      weight: b?.weight,
      experience: b?.experience,
      rosterLevel: b?.level ?? '',
      pendingPositionChange: b?.pendingPositionChange,
      retired: b?.isRetired ?? false,
      debut: b?.firstGamePlayed,
      debutGameId: b?.firstGameId,
      lastGame: b?.lastGamePlayed,
      lastGameId: b?.lastGameId,
    },
    contract: {
      salary: b?.salary ?? 0,
      salaryBase: b?.salaryBase ?? 0,
      nextSeasonSalary: b?.nextSeasonSalary,
      nextSeasonSalaryBase: b?.nextSeasonSalaryBase,
      // Sim-determined "retiring after this season" flag (#360, now on PlayerViewBio).
      retiring: b?.isRetiringNextSeason ?? false,
    },
    acquisition: {
      acquiredBy: b?.acquiredBy ?? '',
      acquiredBySeason: b?.acquiredBySeason,
      tradeId: b?.acquiredByTradeId,
      draftRound: b?.draftRound,
      draftPick: b?.draftPick,
      draftYear: b?.draftYear,
      draftTeam: b?.draftTeam,
      draftTeamId: b?.draftTeamId,
    },
    ratings: {
      attrs: mapRatings(api.ratings),
      pitches: api.pitchTypes ?? null,
      scoutingOn: api.scoutingOn ?? false,
      // Raw endurance ints (#779) live on the EN rating, alongside the projected
      // display grade — kept separate so Stam%/SF divide off the real numbers.
      condition: {
        cRatingEn: api.ratings?.en?.cRatingEn,
        ratingEnRaw: api.ratings?.en?.ratingEnRaw,
        seasonFatigue: api.ratings?.en?.seasonFatigue,
      },
    },
    stats: {
      currentSeason: current ? mapCareerRow(current, isPitcher) : null,
      career: career ? mapCareerRow(career, isPitcher, 'Career') : null,
    },
    statLeaders: mapSeasonFlags(api.highlights, LDR_TO_KEY),
    statRecords: mapSeasonFlags(api.highlights, REC_TO_KEY),
    canManage: Boolean(api.canRename || api.canReassign),
    canRename: api.canRename ?? false,
    canReassign: api.canReassign ?? false,
    reassignTeams: (api.reassignTeams ?? []).map((t) => ({
      id: t.id ?? 0,
      city: t.city ?? '',
      name: t.name ?? '',
    })),
  }
}
